import { FC, useCallback, useEffect, useState } from "react";
import { TDepositHistoryStatus } from "@/context/GummiwormContext/hooks/useDepositTxs";
import {
  getDepositStageStates,
  getRefundStageStates,
  isRefundFlow,
  type TStageState,
} from "@/lib/depositLifecycle";
import { useBrandName } from "@/hooks/useBrandName";
import { RefundDownloadButton } from "@/components/RefundDownloadButton";

interface ProcessingStepProps {
  requestId: string | null;
  status: TDepositHistoryStatus | null;
  error: string | null;
  estimatedAbsorptionTime?: number;
  amountLabel?: string;
  txHash?: string;
  /** The deposit's L2 block has been hard confirmed (multisigned by the head peers) */
  hardConfirmed?: boolean;
  /** L1 tx id of the provisioned multisigned refund tx */
  refundTxHash?: string;
  /** CBOR of the provisioned refund tx, offered for download */
  refundCbor?: string;
}

/** Stage 0 – Requesting → Approved once the deposit's block is hard confirmed
 *  Stage 1 – Provisioning refund → Refund provisioned once we hold the
 *            multisigned refund tx (shown + downloadable in the info rows)
 *  Stage 2 – Submitting → Submitted once the L1 tx is confirmed
 *  Stage 3 – Waiting for Settlement → Available once absorbed
 */
const STAGES = [
  { key: "approved", label: "Approved", activeLabel: "Requesting" },
  { key: "refund", label: "Refund provisioned", activeLabel: "Provisioning refund" },
  { key: "submitted", label: "Submitted", activeLabel: "Submitting" },
  { key: "available", label: "Available", activeLabel: "Waiting for Settlement" },
] as const;

/** Timeline for a rejected deposit's refund (see getRefundStageStates). The
 *  deposit stepper is swapped for this one once the deposit enters the refund
 *  flow — the refund is post-dated, submitted automatically once valid, then
 *  monitored to on-chain confirmation. */
const REFUND_STAGES = [
  { key: "window", label: "Refund window open", activeLabel: "Waiting for refund window" },
  { key: "submitted", label: "Refund submitted", activeLabel: "Submitting refund" },
  { key: "onChain", label: "Refund on chain", activeLabel: "Confirming refund" },
] as const;

interface TimelineStage {
  label: string;
  activeLabel: string;
  state: TStageState;
}

function CopyButton({ text }: { text: string }) {
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
      title="Copy"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
    </button>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="#170B10"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type BadgeVariant = "done" | "active" | "waiting" | "failed";

function StatusBadge({
  variant,
  label,
  className,
}: {
  variant: BadgeVariant;
  label?: string;
  className?: string;
}) {
  const config = {
    done: { label: "Done", className: "bg-sr-mint/15 text-sr-mint" },
    active: { label: "In progress", className: "bg-sr-tangerine/15 text-sr-tangerine" },
    waiting: { label: "Waiting", className: "bg-sr-neutral-700/30 text-sr-neutral-700" },
    failed: { label: "Failed", className: "bg-sr-salmon/15 text-sr-salmon" },
  };
  const { label: defaultLabel, className: baseClassName } = config[variant];
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${baseClassName} ${className ?? ""}`}>
      {label ?? defaultLabel}
    </span>
  );
}

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return "any moment now";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m > 0) return `~${m}m ${s.toString().padStart(2, "0")}s`;
  return `~${s}s`;
}

export const ProcessingStep: FC<ProcessingStepProps> = ({
  requestId,
  status,
  error,
  estimatedAbsorptionTime,
  amountLabel,
  txHash,
  hardConfirmed,
  refundTxHash,
  refundCbor,
}) => {
  const brandName = useBrandName();
  // A deposit rejected after reaching L1 enters the refund flow (rejected →
  // refunding → refunded), which has its own timeline. "failed" (never reached
  // L1, nothing to refund) stays the terminal failure view.
  const inRefundFlow = status != null && isRefundFlow(status);
  const isFailed = status === "failed";
  const isRefunded = status === "refunded";
  const isComplete = status === "absorbed";
  const stageStates = getDepositStageStates({
    status: status ?? "new",
    hardConfirmed,
    refundCbor: refundCbor ?? "",
    refundTxHash,
  });
  const refundStates = getRefundStageStates(status ?? "rejected");
  const timeline: TimelineStage[] = inRefundFlow
    ? REFUND_STAGES.map((s) => ({
        label: s.label,
        activeLabel: s.activeLabel,
        state: refundStates[s.key],
      }))
    : STAGES.map((s) => ({
        label: s.label,
        activeLabel: s.activeLabel,
        state: stageStates[s.key],
      }));
  const explorerHref = txHash
    ? `https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${txHash}`
    : null;
  // What the wait actually is, so a couple of quiet minutes read as the
  // protocol working rather than a hang.
  const statusDetail = isFailed
    ? { variant: "failed" as const, label: "Failed", hint: null }
    : isRefunded
      ? { variant: "done" as const, label: "Refunded", hint: null }
      : inRefundFlow
        ? status === "refunding"
          ? {
              variant: "active" as const,
              label: "Refund on chain",
              hint: "Your refund transaction is confirming on Cardano.",
            }
          : {
              variant: "active" as const,
              label: "Refund pending",
              hint: "Your deposit was rejected. Its refund is post-dated — we submit it automatically once its validity window opens, then wait for it to confirm on-chain.",
            }
    : isComplete
      ? { variant: "done" as const, label: "Available", hint: null }
      : stageStates.approved !== "done"
        ? {
            variant: "active" as const,
            label: "Requesting",
            hint: "The network is countersigning your refund guarantee — usually 2–3 minutes. Your deposit submits automatically once it arrives.",
          }
        : stageStates.refund !== "done"
          ? {
              variant: "active" as const,
              label: "Provisioning refund",
              hint: "Assembling the multisigned refund that keeps your ADA recoverable at all times.",
            }
          : stageStates.submitted !== "done"
            ? {
                variant: "active" as const,
                label: "Submitting",
                hint: "Waiting for the Cardano transaction to confirm.",
              }
            : {
                variant: "active" as const,
                label: "Waiting for Settlement",
                hint: "Your deposit is maturing on-chain and will be credited once the network considers it final.",
              };

  // Live countdown
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!estimatedAbsorptionTime) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      setRemaining(Math.max(0, estimatedAbsorptionTime - now));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [estimatedAbsorptionTime]);

  return (
    <div className="sr-card w-full rounded-[32px] px-4 pt-3 pb-6 sm:p-8">
      {/* Header row */}
      <div className="flex items-start justify-between mb-4">
        <h2
          className="text-[22px] font-medium text-white tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {isFailed
            ? "Deposit Failed"
            : isRefunded
              ? "Deposit Refunded"
              : inRefundFlow
                ? "Refunding Deposit"
                : isComplete
                  ? "Funds Deposited!"
                  : "Deposit in Progress"}
        </h2>
      </div>

      {/* Deposit info — the rows render even without an amount, so a deposit
          opened from the history list still shows its request id etc. */}
      <div className="mb-8">
        {amountLabel && (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-sr-pink-muted">
              Amount
            </span>
            <p
              className="text-[32px] font-medium leading-none tracking-tight text-white sm:text-[38px]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {amountLabel}
            </p>
          </div>
        )}

        <div className={`${amountLabel ? "mt-6" : "mt-2"} grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 sm:gap-x-8`}>
            <span className="text-sm text-sr-pink-muted">To</span>
            <span className="text-sm font-medium text-sr-bubblegum">
              {brandName === "Sugar Rush" ? "Sugar Rush Trading Vault" : "Gummiworm Vault"}
            </span>

            <span className="text-sm text-sr-pink-muted">Status</span>
            <div className="flex flex-col gap-1">
              <div className="flex items-center">
                <StatusBadge
                  variant={statusDetail.variant}
                  label={statusDetail.label}
                />
              </div>
              {statusDetail.hint && (
                <p className="text-xs leading-relaxed text-sr-pink-muted">
                  {statusDetail.hint}
                </p>
              )}
            </div>

            {requestId && (
              <>
                <span className="text-sm text-sr-pink-muted">Request ID</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {requestId}
                  </span>
                  <CopyButton text={requestId} />
                </div>
              </>
            )}

            {refundTxHash && (
              <>
                <span className="text-sm text-sr-pink-muted">Refund</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {refundTxHash.slice(0, 8)}...{refundTxHash.slice(-8)}
                  </span>
                  <CopyButton text={refundTxHash} />
                  {refundCbor && (
                    <RefundDownloadButton hash={refundTxHash} cbor={refundCbor} />
                  )}
                </div>
              </>
            )}

            {txHash && (
              <>
                <span className="text-sm text-sr-pink-muted">Transaction</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {txHash.slice(0, 8)}...{txHash.slice(-8)}
                  </span>
                  <CopyButton text={txHash} />
                  {explorerHref && (
                    <a
                      href={explorerHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
                    >
                      <ExternalLinkIcon />
                    </a>
                  )}
                </div>
              </>
            )}
        </div>
      </div>

      {/* Countdown */}
      {!isFailed && !isComplete && remaining !== null && (
        <div className="flex items-center gap-2 mb-6">
          <span
            className="text-lg font-medium text-sr-lemon-muted tabular-nums"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {formatCountdown(remaining)}
          </span>
          <span className="text-xs text-sr-neutral-500">
            estimated time remaining
          </span>
        </div>
      )}

      {/* Vertical stepper */}
      <div className="flex flex-col">
        {timeline.map((stage, i) => {
          const state = stage.state;
          const isDone = state === "done";
          const isActive = state === "active" && !isFailed;
          const isFuture = !isDone && !isActive && i !== timeline.length - 1;
          const variant: BadgeVariant = isDone
            ? "done"
            : isActive
              ? "active"
              : "waiting";

          return (
            <div key={i} className="flex items-center gap-4">
              {/* Icon + connector */}
              <div className="flex flex-col items-center self-stretch">
                {isDone ? (
                  <div className="w-8 h-8 rounded-full bg-sr-mint/70 flex items-center justify-center shrink-0">
                    <CheckIcon />
                  </div>
                ) : isActive ? (
                  <div className="w-8 h-8 rounded-full border-2 border-sr-tangerine border-t-transparent animate-spin shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full border border-sr-neutral-700/70 shrink-0" />
                )}
                {i < timeline.length - 1 && (
                  <div
                    className={`w-px flex-1 min-h-[24px] ${
                      isDone ? "bg-sr-mint/15" : "bg-sr-neutral-700/20"
                    }`}
                  />
                )}
              </div>

              {/* Content */}
              <div className={`flex-1 flex items-center min-h-8 ${i < timeline.length - 1 ? "pb-5" : ""}`}>
                <div className="flex w-full items-center justify-between">
                  <span
                    className={`text-sm font-medium ${
                      isActive
                        ? "text-white"
                        : isDone
                          ? "text-white/60"
                          : "text-sr-neutral-700/70"
                    }`}
                  >
                    {isActive ? stage.activeLabel : stage.label}
                  </span>
                  {i === timeline.length - 1 && (isComplete || isRefunded) ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sr-mint/15 text-sr-mint">
                      {isRefunded ? "Refunded!" : "Complete!"}
                    </span>
                  ) : (
                    <StatusBadge
                      variant={variant}
                      className={
                        isDone
                          ? "opacity-70"
                          : isFuture
                            ? "opacity-45"
                            : ""
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Failure error, or the rejection reason while the refund runs. In the
          refund flow it is context (the deposit is being made whole), so it is
          muted rather than alarming. */}
      {isFailed && error && (
        <div className="mt-6 rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{error}</p>
        </div>
      )}
      {inRefundFlow && error && (
        <div className="mt-6 rounded-xl bg-white/[0.03] p-3">
          <p className="text-xs text-sr-pink-muted">
            <span className="text-sr-neutral-500">Rejection reason: </span>
            {error}
          </p>
        </div>
      )}
    </div>
  );
};
